/**
 * Session context — REAL BetterAuth session (P1).
 *
 * Flow: cookie session (BetterAuth) → active organization → short-lived API JWT
 * (GET /api/betterauth/token) carrying org + app role, sent as `Authorization:
 * Bearer` by `apiFetch`. Tenancy stays server-derived from the verified token.
 *
 * Status drives the shell:
 *   'loading'   — restoring the session on first paint
 *   'signedOut' — no cookie session → /signin
 *   'needsOrg'  — signed in but member of no organization → create-org gate
 *   'ready'     — org active + token minted → the app renders
 *
 * Roles map to the scoped model: manager (= BetterAuth owner/admin) · engineer (= member).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  activeMemberRole,
  fetchToken,
  getSession,
  listOrgs,
  setActiveOrg,
  signOut as apiSignOut,
  type OrgSummary,
} from './authApi';

export type Role = 'manager' | 'engineer';
export type SessionStatus = 'loading' | 'signedOut' | 'needsOrg' | 'ready';

export interface Session {
  organizationId: string;
  orgName: string;
  userRef: string;
  userName: string;
  email: string;
  role: Role;
  token: string | null;
}

interface SessionCtx {
  status: SessionStatus;
  session: Session;
  orgs: OrgSummary[];
  /** Re-read cookie session + orgs + token (after sign-in / org creation). */
  refresh: () => Promise<void>;
  switchOrg: (organizationId: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Legacy no-op kept for interface compat (role now comes from membership). */
  setRole: (r: Role) => void;
}

const Ctx = createContext<SessionCtx | null>(null);

const EMPTY: Session = {
  organizationId: '',
  orgName: '',
  userRef: '',
  userName: '',
  email: '',
  role: 'engineer',
  token: null,
};

const TOKEN_REFRESH_MS = 10 * 60_000; // JWT lives 15 min — refresh at 10

/** ZERO-PII display handle: stored name is '' by design — derive from the e-mail. */
export function displayHandle(name: string | null | undefined, email: string): string {
  const n = (name ?? '').trim();
  if (n) return n; // legacy rows only
  const local = email.split('@')[0] ?? 'member';
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export function appRole(orgRole: string | null): Role {
  return orgRole === 'owner' || orgRole === 'admin' ? 'manager' : 'engineer';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<Session>(EMPTY);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const info = await getSession();
    if (!info) {
      setSession(EMPTY);
      setOrgs([]);
      setStatus('signedOut');
      return;
    }
    const myOrgs = await listOrgs();
    setOrgs(myOrgs);
    if (myOrgs.length === 0) {
      setSession({ ...EMPTY, userRef: info.user.id, userName: displayHandle(info.user.name, info.user.email), email: info.user.email });
      setStatus('needsOrg');
      return;
    }
    let activeId = info.activeOrganizationId;
    if (!activeId || !myOrgs.some((o) => o.id === activeId)) {
      // No active org on the session yet — activate the first membership.
      const first = myOrgs[0];
      if (first) {
        await setActiveOrg(first.id);
        activeId = first.id;
      }
    }
    const active = myOrgs.find((o) => o.id === activeId) ?? myOrgs[0];
    const [role, token] = await Promise.all([activeMemberRole(), fetchToken()]);
    if (!active) {
      setStatus('needsOrg');
      return;
    }
    setSession({
      organizationId: active.id,
      orgName: active.name,
      userRef: info.user.id,
      userName: displayHandle(info.user.name, info.user.email),
      email: info.user.email,
      role: appRole(role),
      token,
    });
    setStatus('ready');
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the short-lived API JWT fresh while the app is open.
  useEffect(() => {
    if (status !== 'ready') return;
    timer.current = setInterval(() => {
      void fetchToken().then((token) => {
        if (token) setSession((s) => ({ ...s, token }));
      });
    }, TOKEN_REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [status]);

  const value = useMemo<SessionCtx>(
    () => ({
      status,
      session,
      orgs,
      refresh: load,
      switchOrg: async (organizationId: string) => {
        await setActiveOrg(organizationId);
        await load();
      },
      signOut: async () => {
        await apiSignOut();
        setSession(EMPTY);
        setOrgs([]);
        setStatus('signedOut');
      },
      setRole: () => undefined,
    }),
    [status, session, orgs, load],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSession outside SessionProvider');
  return c;
}

export const canManage = (r: Role): boolean => r === 'manager';
